import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Mail, ArrowLeft, CheckCircle, Loader2, AlertCircle, Sparkles } from 'lucide-react';
import { validateEmail, getValidationState } from '../../utils/emailValidation';

const LoginPage = () => {
  const { requestMagicLink } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Initialize state from URL params
  const emailParam = searchParams.get('email');
  const destParam = searchParams.get('dest');
  const promptParam = searchParams.get('prompt');
  const fromParam = searchParams.get('from');
  
  const isFreeTierSignup = fromParam === 'free-tier';
  
  const [email, setEmail] = useState(emailParam || '');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [emailFeedback, setEmailFeedback] = useState(null);
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  
  // Quick login mode: email pre-filled from URL with redirect destination
  const [isQuickLogin, setIsQuickLogin] = useState(!!emailParam);
  const redirectDest = destParam;
  const promptId = promptParam;

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setErrorMessage('');
    setEmailSuggestion(null);
    setStatus('idle');
    
    // Real-time feedback (only show after user has typed enough)
    if (value.length > 4) {
      const validationState = getValidationState(value, true);
      setEmailFeedback(validationState.message || null);
      if (validationState.suggestion) {
        setEmailSuggestion(validationState.suggestion);
      }
    } else {
      setEmailFeedback(null);
    }
  };

  const handleUseSuggestion = () => {
    if (emailSuggestion) {
      setEmail(emailSuggestion);
      setEmailSuggestion(null);
      setEmailFeedback(null);
      setErrorMessage('');
      setStatus('idle');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailFeedback(null);
    setEmailSuggestion(null);

    // Validate email before submission
    const validation = validateEmail(email);
    if (!validation.isValid) {
      setStatus('error');
      setErrorMessage(validation.message);
      if (validation.suggestion) {
        setEmailSuggestion(validation.suggestion);
      }
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      // Pass redirect destination and prompt ID for quick login flow
      await requestMagicLink(email, redirectDest, promptId);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.message || 'Failed to send login link');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col">
      {/* Header */}
      <header className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link to="/it-leadership" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to site</span>
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Logo/Brand */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">30 Day Skills</h1>
            {isFreeTierSignup ? (
              <p className="text-zinc-400">Start your free leadership coaching journey</p>
            ) : (
              <p className="text-zinc-400">Sign in to your dashboard</p>
            )}
          </div>

          {/* Free Tier Welcome Banner */}
          {isFreeTierSignup && status !== 'success' && (
            <div className="mb-6 p-4 bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 rounded-lg">
              <h3 className="text-emerald-400 font-semibold mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Free Starter Plan Includes:
              </h3>
              <ul className="text-zinc-300 text-sm space-y-1">
                <li>• 10 AI coaching reflections per month</li>
                <li>• Track 5 people in your work network</li>
                <li>• 30-day AI memory window</li>
                <li>• Access via web, email, or Telegram</li>
              </ul>
            </div>
          )}

          {/* Card */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8">
            {status === 'success' ? (
              /* Success state */
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">Check your email</h2>
                <p className="text-zinc-400 mb-4">
                  We&apos;ve sent a sign-in link to <span className="text-white font-medium">{email}</span>
                </p>
                <p className="text-zinc-500 text-sm">
                  The link will expire in 15 minutes
                </p>
                <button
                  onClick={() => {
                    setStatus('idle');
                    setEmail('');
                    setIsQuickLogin(false);
                  }}
                  className="mt-6 text-red-500 hover:text-red-400 transition-colors text-sm"
                >
                  Use a different email
                </button>
              </div>
            ) : isQuickLogin && email ? (
              /* Quick login mode - streamlined single-click UI */
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-8 h-8 text-red-500" />
                  </div>
                  <h2 className="text-xl font-semibold text-white mb-2">Welcome back!</h2>
                  <p className="text-zinc-400">
                    Click below to access your Companion
                  </p>
                </div>

                <div className="bg-zinc-800 rounded-lg p-4 mb-6">
                  <p className="text-zinc-400 text-sm mb-1">Signing in as</p>
                  <p className="text-white font-medium">{email}</p>
                </div>

                {status === 'error' && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                      <p className="text-red-400 text-sm">{errorMessage}</p>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={status === 'loading'}
                  className="w-full py-4 bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 text-lg"
                  data-testid="quick-login-button"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending link...
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      Send me a sign-in link
                    </>
                  )}
                </button>

                <button
                  onClick={() => {
                    setIsQuickLogin(false);
                    setEmail('');
                  }}
                  className="w-full mt-4 text-zinc-500 hover:text-zinc-300 transition-colors text-sm"
                >
                  Use a different email address
                </button>
              </>
            ) : (
              /* Form state */
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Sign in with email</h2>
                    <p className="text-zinc-500 text-sm">No password needed</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label htmlFor="email" className="block text-sm font-medium text-zinc-300 mb-2">
                      Email address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={handleEmailChange}
                      placeholder="you@example.com"
                      required
                      disabled={status === 'loading'}
                      className={`w-full px-4 py-3 bg-zinc-800 border rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 disabled:opacity-50 ${
                        status === 'error' || emailFeedback ? 'border-amber-500' : 'border-zinc-700'
                      }`}
                      data-testid="login-email-input"
                    />
                    {/* Real-time validation feedback */}
                    {emailFeedback && status !== 'error' && (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
                        <span className="text-amber-400">{emailFeedback}</span>
                        {emailSuggestion && (
                          <button
                            type="button"
                            onClick={handleUseSuggestion}
                            className="text-blue-400 hover:text-blue-300 underline font-medium"
                            data-testid="login-use-suggestion"
                          >
                            Use this
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {status === 'error' && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                        <p className="text-red-400 text-sm">{errorMessage}</p>
                        {emailSuggestion && (
                          <button
                            type="button"
                            onClick={handleUseSuggestion}
                            className="text-blue-400 hover:text-blue-300 underline font-medium text-sm"
                            data-testid="login-use-suggestion-error"
                          >
                            Use this
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading' || !email}
                    className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                    data-testid="login-submit-button"
                  >
                    {status === 'loading' ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending link...
                      </>
                    ) : (
                      'Send sign-in link'
                    )}
                  </button>
                </form>

                <p className="mt-6 text-center text-zinc-500 text-sm">
                  We&apos;ll email you a magic link for instant sign-in
                </p>
              </>
            )}
          </div>

          {/* Footer */}
          <p className="mt-8 text-center text-zinc-600 text-sm">
            By signing in, you agree to our{' '}
            <Link to="/terms-of-use" className="text-zinc-400 hover:text-white">Terms</Link>
            {' '}and{' '}
            <Link to="/privacy-policy" className="text-zinc-400 hover:text-white">Privacy Policy</Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
